<template>
  <div 
    class="ssml-editor"
    @mouseup="handleMouseUp"
    @keyup="handleKeyUp"
  >
    <div class="content">
      <div
        ref="editableContent"
        id="article"
        class="editable-area"
        contenteditable="true"
        @input="handleInput"
        @paste="handlePaste"
        @click="handleClick"
        @focus="handleFocus"
        @blur="handleBlur"
        :class="{ placeholder: !content && !hasBreak }"
        v-html="content"
      ></div>
      
      <floating-controls
        ref="floatingControls"
        @speed-confirm="handleSpeedConfirm"
        @tone-confirm="handleToneConfirm"
      />
      
      <!-- <div class="input-number">
        <span :class="{ warning: isOverLimit }">
          {{ currentLength }}
        </span>
        / {{ maxLength }}
      </div> -->
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { Message } from 'element-ui'
import FloatingControls from './FloatingControls.vue'
import { DIGIT_TYPES, PAUSE_OPTIONS } from '../constants/ssml'

export default {
  name: 'SsmlEditor',

  inject: ['store'],

  components: {
    FloatingControls
  },

  props: {
    maxLength: {
      type: Number,
      default: 20000
    },
    defaultVoice: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      hasBreak: false,
      currentLength: 0,
      savedRange: null,
      innerContent: '',
      isScrolling: false,
      scrollTimer: null
    }
  },

  computed: {
    content: {
      get() {
        return this.store.state.ssmlEditor.content
      },
      set(val) {
        this.store.commit('ssmlEditor/SET_CONTENT', val)
      }
    },
    isOverLimit() {
      return this.currentLength > this.maxLength
    },
    isFocusInsideEditor() {
      return this.store.state.ssmlEditor.isFocusInsideEditor
    }
  },

  methods: {
    handleInput(e) {
      // 调用 updateWordCount 更新实际字数
      this.updateWordCount()
    },

    handlePaste(e) {
      e.preventDefault();
      
      // 获取剪贴板中的纯文本内容
      const text = (e.clipboardData || window.clipboardData).getData('text');
      
      // 如果有选中内容，先删除
      const selection = window.getSelection();
      if (selection.rangeCount) {
        const range = selection.getRangeAt(0);
        if (!range.collapsed) {
          range.deleteContents();
        }
        
        // 插入纯文本
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);
        
        // 移动光标到插入内容后
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      
      // 触发内容更新
      this.handleInput({ target: this.$refs.editableContent });
    },

    handleFocus() {
      this.store.commit('ssmlEditor/SET_FOCUS_STATE', true)
    },

    handleBlur() {
      // 使用一个变量跟踪是否正在滚动
      this.isScrolling = true
      
      setTimeout(() => {
        this.isScrolling = false
        
        // 只有当不是因为滚动引起的失焦才隐藏控件
        if (!document.activeElement.closest('.operators') && !this.isScrollingAction) {
          this.store.commit('ssmlEditor/SET_FOCUS_STATE', false)
          // 添加隐藏浮动控件逻辑，但排除滚动导致的失焦
          if (!this.isScrolling) {
            this.hideFloatingControls()
          }
        }
      }, 200)
    },

    handleMouseUp() {
      this.saveSelection()
      
      // 选区变化检查
      const selection = window.getSelection()
      if (selection.rangeCount === 0 || selection.toString().trim() === '') {
        // 如果没有选中内容，确保浮动控件隐藏
        if (this.$refs.floatingControls && this.$refs.floatingControls.visible) {
          this.$refs.floatingControls.hide()
        }
      }
    },

    handleKeyUp() {
      this.saveSelection()
    },

    handleClick(e) {
      e.stopPropagation()
      this.store.commit('ssmlEditor/SET_FOCUS_STATE', true)
      this.saveSelection()
    },

    saveSelection() {
      const selection = window.getSelection()
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0)
        let commonAncestor = range.commonAncestorContainer
        let isFocusInsideEditor = this.focusInsideEditor(commonAncestor)
        
        this.store.commit('ssmlEditor/SET_FOCUS_STATE', isFocusInsideEditor)
        
        if (isFocusInsideEditor) {
          this.store.commit('ssmlEditor/SET_RANGE', range)
        }
      }
    },
    focusInsideEditor(node) {
      let flag = true
      let result = false
      
      while (flag && !result) {
        if (!node || !node.parentNode) {
          flag = false
          break
        }
        
        if (node.id === 'article') {
          flag = false
          result = true
        } else {
          node = node.parentNode
        }
      }
      
      return result
    },

    // 创建删除按钮
    createDeleteButton(wrapperClass) {
      const deleteBtn = document.createElement('i')
      deleteBtn.className = 'delete-btn'
      const img = document.createElement('img')
      img.src = require('@/assets/img/voiceComposeCustom/delete.svg')
      img.style.width = '12px'
      img.style.height = '12px'
      deleteBtn.appendChild(img)
      deleteBtn.style.pointerEvents = 'auto'
      img.style.pointerEvents = 'none'
      deleteBtn.style.zIndex = '200'
      // 不再绑定onclick
      return deleteBtn
    },

    // 创建类型标签
    createTypeLabel(displayType, labelClass = '') {
      const typeSpan = document.createElement('span')
      typeSpan.className = `type-label ${labelClass}`
      typeSpan.appendChild(document.createTextNode(displayType))
      return typeSpan
    },

    // 创建带框选的元素
    createWrappedElement(element, text, attrs = {}) {
      // 设置属性
      Object.entries(attrs).forEach(([key, value]) => {
        element.setAttribute(key, value)
      })
      element.textContent = text

      // 创建wrap容器并禁用编辑
      const wrapDiv = document.createElement(`${element.tagName.toLowerCase()}-wrap`)
      wrapDiv.setAttribute('contenteditable', 'false')
      
      // 添加类型标签
      const labelClass = this.getLabelClass(attrs)
      const typeLabel = this.createTypeLabel(attrs['display-type'], labelClass)
      wrapDiv.appendChild(typeLabel)

      // 添加删除按钮
      const deleteBtn = this.createDeleteButton(`${element.tagName.toLowerCase()}-wrap`)
      wrapDiv.appendChild(deleteBtn)

      // 添加框选
      wrapDiv.appendChild(document.createTextNode('【'))
      wrapDiv.appendChild(element)
      wrapDiv.appendChild(document.createTextNode('】'))

      return wrapDiv
    },

    // 获取标签样式类名
    getLabelClass(attrs) {
      if (attrs.rate !== undefined) return 'speed-label'
      if (attrs.pitch !== undefined) return 'tone-label'
      if (attrs.name !== undefined) return 'voice-label'
      if (attrs.ph !== undefined) return 'phoneme-label'
      return ''
    },

    insertTag(type, parameters) {
      const isEditorFocused = this.store.state.ssmlEditor.isFocusInsideEditor
      if (!isEditorFocused) {
        this.$refs.editableContent.focus()
      }

      // 获取/恢复选区
      const range = this.store.state.ssmlEditor.savedRange || window.getSelection().getRangeAt(0)
      if (this.store.state.ssmlEditor.savedRange) {
        const selection = window.getSelection()
        selection.removeAllRanges()
        selection.addRange(this.store.state.ssmlEditor.savedRange)
      }

      if (type === 'phoneme') {
        // 创建phoneme-wrap元素
        const phonemeWrap = document.createElement('phoneme-wrap');
        phonemeWrap.dataset.pinyin = parameters.pinyin || '';
        phonemeWrap.dataset.tone = parameters.tone || '';
        
        // 设置内部HTML
        const selectedText = range.toString();
        
        // 在内容后添加拼音和音调标记
        phonemeWrap.innerHTML = `
          <span class="content">${selectedText}</span>
          <span class="phoneme-label">[${parameters.pinyin}${parameters.tone}]</span>
          <span class="delete-btn">×</span>
        `;
        
        // 删除原始范围内容
        range.deleteContents();
        
        // 插入新创建的元素
        range.insertNode(phonemeWrap);
        
        // 为删除按钮添加事件
        const deleteBtn = phonemeWrap.querySelector('.delete-btn');
        if (deleteBtn) {
          deleteBtn.addEventListener('click', this.removeTag);
        }
        
        // 更新选择范围和字数统计
        this.updateSelection();
        this.updateWordCount();
        
        return;
      }
      
      // 创建标签元素
      const element = document.createElement(type)
      const wrapDiv = document.createElement(`${type}-wrap`)
      
      // 根据标签类型处理
      const replaceTypes = ['say-as', 'prosody', 'voice', 'phoneme']
      const insertTypes = ['break', 'recycle']

      if (insertTypes.includes(type)) {
        // 设置属性
        Object.entries(parameters).forEach(([key, value]) => {
          // if (key !== 'text') {
            element.setAttribute(key, value)
          // }
        })
        // element.textContent = parameters.text || ''
        wrapDiv.setAttribute('contenteditable', 'false')
        wrapDiv.appendChild(element)
        range.insertNode(wrapDiv)
      } else if (replaceTypes.includes(type)) {
        console.log("range", range)
        // 替换类标签
        if (!range.toString().trim() && type !== 'phoneme') {
          Message.warning('请先选择要操作的文本')
          return
        }

        // 保存选区内的 HTML 内容
        const container = document.createElement('div')
        const clonedContents = range.cloneContents()
        container.appendChild(clonedContents)
        
        // 创建新元素并设置内部 HTML
        Object.entries(parameters).forEach(([key, value]) => {
          element.setAttribute(key, value)
        })
        
        // 对于phoneme标签，使用指定的text
        if (type === 'phoneme') {
          element.textContent = parameters.text || ''
        } else {
          element.innerHTML = container.innerHTML // 保留内部的标签
        }

        // 创建包装器
        wrapDiv.setAttribute('contenteditable', 'false')
        
        // 添加类型标签
        const labelClass = this.getLabelClass(parameters)
        const typeLabel = this.createTypeLabel(parameters['display-type'], labelClass)
        wrapDiv.appendChild(typeLabel)

        // 添加删除按钮
        const deleteBtn = this.createDeleteButton(`${type}-wrap`)
        wrapDiv.appendChild(deleteBtn)

        // 添加框选和内容
        wrapDiv.appendChild(document.createTextNode('【'))
        wrapDiv.appendChild(element)
        wrapDiv.appendChild(document.createTextNode('】'))

        // 替换选区内容
        range.deleteContents()
        range.insertNode(wrapDiv)

        // 更新选区位置
        if (wrapDiv.parentNode) {
          const newRange = document.createRange()
          newRange.setStartAfter(wrapDiv)
          newRange.collapse(true)
          const selection = window.getSelection()
          selection.removeAllRanges()
          selection.addRange(newRange)
        }
      }

      this.handleInput({ target: this.$refs.editableContent })
    },

    reset() {
      const editableContent = this.$refs.editableContent
      
      // 递归处理嵌套标签
      const triggerInnerDelete = (element) => {
        // 获取最外层的包装器
        const outerWraps = Array.from(element.querySelectorAll('[contenteditable="false"]'))
          .filter(wrap => {
            // 找出没有其他包装器作为父元素的包装器
            const parent = wrap.parentElement
            return !parent || !parent.closest('[contenteditable="false"]')
          })

        // 处理最外层包装器
        outerWraps.forEach(wrap => {
          const deleteBtn = wrap.querySelector('.delete-btn')
          if (deleteBtn) {
            deleteBtn.click()
          }
        })
      }

      // 首先删除最外层的替换类标签
      triggerInnerDelete(editableContent)
      
      // 然后删除插入类标签
      const insertTags = ['break-wrap', 'recycle-wrap']
      insertTags.forEach(tag => {
        const elements = editableContent.getElementsByTagName(tag)
        while (elements.length > 0) {
          elements[0].remove()
        }
      })

      this.handleInput({ target: editableContent })
    },

    // 统一的文本提取核心方法
    extractPlainText(node, options = {}) {
      const defaults = {
        removeBreaks: true,       // 是否移除停顿标签
        removeFormatting: true,   // 是否移除【】等格式标记
        removeWhitespace: false   // 是否移除空白字符
      };
      
      const settings = {...defaults, ...options};
      
      // 递归处理节点
      const processNode = (node) => {
        // 处理文本节点
        if (node.nodeType === Node.TEXT_NODE) {
          let text = node.textContent || '';
          if (settings.removeFormatting) {
            text = text.replace(/【|】/g, '');
          }
          return text;
        }
        
        // 处理元素节点
        if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName.toLowerCase();
          
          // 忽略UI元素
          if (node.classList && (
              node.classList.contains('type-label') || 
              node.classList.contains('delete-btn'))) {
            return '';
          }
          
          // 忽略停顿和连续标签
          if (settings.removeBreaks && 
              (tagName === 'break-wrap' || tagName === 'recycle-wrap')) {
            return '';
          }
          
          // 处理包含内容的标签
          if (tagName.endsWith('-wrap')) {
            // 处理phoneme标签
            if (tagName === 'phoneme-wrap') {
              const phoneme = node.querySelector('phoneme');
              if (phoneme) {
                return phoneme.textContent || '';
              }
            }
            
            const innerTag = tagName.replace('-wrap', '');
            const inner = node.querySelector(innerTag);
            if (inner) {
              return processNode(inner);
            }
          }
          
          // 递归处理子节点
          let result = '';
          Array.from(node.childNodes).forEach(child => {
            result += processNode(child);
          });
          return result;
        }
        
        return '';
      };
      
      // 处理输入数据
      let root;
      if (node.nodeType === Node.DOCUMENT_FRAGMENT_NODE || node.nodeType === Node.ELEMENT_NODE) {
        root = node;
      } else {
        // 处理HTML字符串或其他类型
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = String(node);
        root = tempDiv;
      }
      
      // 获取处理后的文本
      let result = processNode(root);
      
      // 根据设置移除空白字符
      if (settings.removeWhitespace) {
        result = result.replace(/\s+/g, '');
      }
      
      return result;
    },

    // 更新字数统计方法
    updateWordCount() {
      try {
        const content = this.$refs.editableContent;
        if (!content) return 0;
        
        // 获取纯文本
        const plainText = this.extractPlainText(content);
        
        // 计算字数(不含空白字符)
        this.currentLength = plainText.replace(/\s+/g, '').length;
        
        return this.currentLength;
      } catch (err) {
        console.error('字数统计错误:', err);
        return 0;
      }
    },

    // 清理所有标签获取纯文本
    getCleanText(element) {
      // 如果是文本节点,直接返回内容  
      if (element.nodeType === Node.TEXT_NODE) {
        return element.textContent
      }

      // 如果是包装器节点,只保留内部纯文本
      if (/-wrap$/.test(element.tagName.toLowerCase())) {
        const innerText = Array.from(element.childNodes)
          .map(node => this.getCleanText(node))
          .join('')
        return innerText.replace(/[【】]/g, '')
      }

      // 递归处理所有子节点
      return Array.from(element.childNodes)
        .map(node => this.getCleanText(node))
        .join('')
    },

    handleDeleteBtnClick(e) {
      // 只处理delete-btn点击
      const btn = e.target.closest('.delete-btn')
      if (!btn) return

      // 找到最近的包装器
      const currentWrap = btn.closest('[contenteditable="false"]')
      if (!currentWrap) return

      // 获取内部标签
      const tagName = currentWrap.tagName.toLowerCase().replace('-wrap', '')
      const inner = currentWrap.querySelector(tagName)
      if (!inner) return

      // 获取父节点
      const parent = currentWrap.parentNode
      if (!parent) return

      // 复制内部内容
      const fragment = document.createDocumentFragment()
      Array.from(inner.childNodes).forEach(child => {
        fragment.appendChild(child)
      })

      // 插入到包装器前
      parent.insertBefore(fragment, currentWrap)
      parent.removeChild(currentWrap)

      // 触发input事件，更新内容
      this.handleInput({ target: this.$refs.editableContent })
      // 阻止冒泡
      e.stopPropagation()
      e.preventDefault()
    },

    // 显示变速浮动控件
    showSpeedControls(range) {
      this.$refs.floatingControls.showSpeed(range)
    },
    
    // 显示变调浮动控件
    showToneControls(range) {
      this.$refs.floatingControls.showTone(range)
    },
    
    // 处理变速确认
    handleSpeedConfirm(data) {
      this.insertTag('prosody', {
        ...data.attrs,
        'display-type': data.attrs['display-type']
      }, data.range.toString(), data.range)
    },
    
    // 处理变调确认
    handleToneConfirm(data) {
      this.insertTag('prosody', {
        ...data.attrs,
        'display-type': data.attrs['display-type']
      }, data.range.toString(), data.range)
    },

    // 添加关闭浮动控件的方法
    hideFloatingControls() {
      if (this.$refs.floatingControls) {
        this.$refs.floatingControls.hide()
      }
    },

    // 添加处理滚动的方法
    handleScroll() {
      // 标记正在滚动
      this.isScrolling = true
      
      // 更新浮动控件位置，而不是隐藏它
      if (this.$refs.floatingControls && this.$refs.floatingControls.visible) {
        this.$refs.floatingControls.adjustPositionIfNeeded()
      }
      
      // 定时器来重置滚动状态
      clearTimeout(this.scrollTimer)
      this.scrollTimer = setTimeout(() => {
        this.isScrolling = false
      }, 150)
    },

    // 递归节点处理方法
    extractPlainTextRecursive(node) {
      // 如果是文本节点，直接返回文本
      if (node.nodeType === Node.TEXT_NODE) {
        // 过滤掉【】等特殊标记
        return node.textContent.replace(/【|】/g, '');
      }
      
      // 忽略这些元素及其内容
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        if (tagName === 'break-wrap' || 
            tagName === 'recycle-wrap' ||
            tagName === 'i' ||
            node.classList.contains('type-label') ||
            node.classList.contains('delete-btn')) {
          return '';
        }
      }
      
      // 递归处理子节点
      let text = '';
      for (let i = 0; i < node.childNodes.length; i++) {
        text += this.extractPlainTextRecursive(node.childNodes[i]);
      }
      
      return text;
    },

    // 处理复制
    handleCopy(e) {
      // 阻止默认行为
      e.preventDefault();
      
      // 获取选中的内容
      const selection = window.getSelection();
      if (!selection.rangeCount) return;
      
      const range = selection.getRangeAt(0);
      
      // 检查选区是否在编辑器内
      const editorElem = this.$refs.editableContent;
      if (!editorElem.contains(range.commonAncestorContainer)) return;
      
      // 创建临时容器来提取纯文本
      const container = document.createElement('div');
      container.appendChild(range.cloneContents());
      
      // 提取纯文本
      const plainText = this.getPlainTextContent(container);
      
      // 设置剪贴板内容
      e.clipboardData.setData('text/plain', plainText);
      
      console.log('复制的内容:', plainText);
    },

    // 纯文本提取
    getPlainTextContent(element) {
      // 创建临时容器克隆元素，以免影响原始DOM
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = element.innerHTML;
      
      // 移除所有不需要的元素
      
      // 1. 移除所有停顿和连续标签
      ['break-wrap', 'recycle-wrap'].forEach(tag => {
        const elements = tempDiv.getElementsByTagName(tag);
        while (elements.length > 0) {
          elements[0].parentNode.removeChild(elements[0]);
        }
      });
      
      // 2. 移除所有的类型标签和删除按钮
      const selectors = ['.type-label', '.delete-btn'];
      selectors.forEach(selector => {
        const elements = tempDiv.querySelectorAll(selector);
        for (let i = 0; i < elements.length; i++) {
          elements[i].parentNode.removeChild(elements[i]);
        }
      });
      
      // 3. 单独处理有内容的标签包装器，保留内部文本
      const contentWrappers = {
        'prosody-wrap': 'prosody',
        'voice-wrap': 'voice',
        'say-as-wrap': 'say-as',
        'phoneme-wrap': 'phoneme'
      };
      
      Object.entries(contentWrappers).forEach(([wrapper, inner]) => {
        const wrappers = tempDiv.getElementsByTagName(wrapper);
        for (let i = wrappers.length - 1; i >= 0; i--) {
          const wrap = wrappers[i];
          const content = wrap.querySelector(inner);
          if (content) {
            // 创建文本节点保存内容
            const text = document.createTextNode(content.textContent || '');
            if (wrap.parentNode) {
              wrap.parentNode.replaceChild(text, wrap);
            }
          } else {
            if (wrap.parentNode) {
              wrap.parentNode.removeChild(wrap);
            }
          }
        }
      });
      
      // 4. 移除特殊格式字符
      let text = tempDiv.textContent || '';
      text = text.replace(/【|】/g, '');
      
      return text;
    },

    handleCut(e) {
      // 处理复制部分
      this.handleCopy(e);
      
      // 如果是剪切，还需要删除选中内容
      if (window.getSelection().rangeCount) {
        const range = window.getSelection().getRangeAt(0);
        range.deleteContents();
        
        // 触发内容更新
        this.handleInput({ target: this.$refs.editableContent });
      }
    },

    // 显示多音字浮动控件
    showPhonemeControls(range) {
      this.$refs.floatingControls.showPhoneme(range);
    },

    // 处理多音字确认
    handlePhonemeConfirm(data) {
      this.insertTag('phoneme', {
        pinyin: data.pinyin,
        tone: data.tone,
        'display-type': `多音字:${data.char}`
      });
    },

    // 添加removeTag方法
    removeTag(event) {
      event.preventDefault();
      event.stopPropagation();
      
      const tagElement = event.target.parentNode;
      const parent = tagElement.parentNode;
      
      // 提取内容节点
      const contentNode = tagElement.querySelector('.content');
      if (contentNode) {
        // 创建文档片段来保存内容
        const fragment = document.createDocumentFragment();
        while (contentNode.firstChild) {
          fragment.appendChild(contentNode.firstChild);
        }
        
        // 替换标签元素
        parent.replaceChild(fragment, tagElement);
        
        // 更新字数统计
        this.$nextTick(() => {
          this.updateWordCount();
        });
      }
    },

    // 添加setupPhonemeWrap方法
    setupPhonemeWrap(node) {
      // 设置内容区域可编辑
      const contentNode = node.querySelector('.content');
      if (contentNode) {
        contentNode.contentEditable = true;
        
        // 监听内容变化，更新字数统计
        contentNode.addEventListener('input', () => {
          this.updateWordCount();
        });
      }
      
      // 确保删除按钮有事件监听
      const deleteBtn = node.querySelector('.delete-btn');
      if (deleteBtn) {
        deleteBtn.addEventListener('click', this.removeTag);
      }
    },

    /**
     * 获取最终SSML字符串
     * @returns {string}
     */
    getFinalSSML() {
      const root = this.$refs.editableContent;
      let ssml = this.domToSSML(root);
      return `<speak>${ssml}</speak>`;
    },

    /**
     * 递归将编辑器DOM转为SSML
     * @param {Node} node
     * @returns {string}
     */
    domToSSML(node) {
      // 文本节点
      if (node.nodeType === Node.TEXT_NODE) {
        // 过滤掉【】等特殊标记
        return node.textContent.replace(/【|】/g, '');
      }

      // 元素节点
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();

        // 跳过UI元素
        if (
          node.classList.contains('type-label') ||
          node.classList.contains('delete-btn')
        ) {
          return '';
        }

        // 处理自定义包装器
        if (tagName === 'prosody-wrap') {
          const prosody = node.querySelector('prosody');
          if (prosody) {
            // 取属性
            const rate = prosody.getAttribute('rate');
            const pitch = prosody.getAttribute('pitch');
            let attrs = '';
            if (rate) attrs += ` rate="${rate}"`;
            if (pitch) attrs += ` pitch="${pitch}"`;
            return `<prosody${attrs}>${this.domToSSML(prosody)}</prosody>`;
          }
        }

        if (tagName === 'voice-wrap') {
          const voice = node.querySelector('voice');
          if (voice) {
            const name = voice.getAttribute('name');
            let attrs = '';
            if (name) attrs += ` name="${name}"`;
            return `<voice${attrs}>${this.domToSSML(voice)}</voice>`;
          }
        }

        if (tagName === 'phoneme-wrap') {
          const phoneme = node.querySelector('.content');
          const pinyin = node.dataset.pinyin || '';
          const tone = node.dataset.tone || '';
          // 火山引擎要求ph属性为拼音+声调，如 "ZHONG4"
          let ph = pinyin + tone;
          return `<phoneme alphabet="py" ph="${ph}">${phoneme ? this.domToSSML(phoneme) : ''}</phoneme>`;
        }

        if (tagName === 'break-wrap') {
          const breakTag = node.querySelector('break');
          if (breakTag) {
            const time = breakTag.getAttribute('time');
            const strength = breakTag.getAttribute('strength');
            let attrs = '';
            if (time) attrs += ` time="${time}"`;
            if (strength) attrs += ` strength="${strength}"`;
            return `<break${attrs}/>`;
          }
          // 兼容旧实现
          const time = node.getAttribute('time');
          const strength = node.getAttribute('strength');
          let attrs = '';
          if (time) attrs += ` time="${time}"`;
          if (strength) attrs += ` strength="${strength}"`;
          return `<break${attrs}/>`;
        }

        if (tagName === 'recycle-wrap') {
          // 连续标签可自定义为 <recycle> 或直接输出"连续"
          // 如果火山引擎无此标签，直接输出文本
          return '连续';
        }

        if (tagName === 'say-as-wrap') {
          const sayAs = node.querySelector('say-as');
          if (sayAs) {
            const interpretAs = sayAs.getAttribute('interpret-as');
            let attrs = '';
            if (interpretAs) attrs += ` interpret-as="${interpretAs}"`;
            return `<say-as${attrs}>${this.domToSSML(sayAs)}</say-as>`;
          }
        }

        // 递归处理子节点
        let result = '';
        node.childNodes.forEach(child => {
          result += this.domToSSML(child);
        });
        return result;
      }

      return '';
    },

    /**
     * 拆分SSML为多个对象，支持voice和prosody rate
     * @param {string} ssml 完整的<speak>...</speak>字符串
     * @param {string} defaultSpeakerId 默认说话人ID
     * @returns {Array} [{speaker_id, tts_text, speed, ...}]
     */
    splitSSMLByVoiceAndSpeed(ssml, defaultSpeakerId) {
      const result = [];

      // 解析SSML
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(ssml, "text/xml");
      const parseError = xmlDoc.querySelector("parsererror");
      if (parseError) {
        console.error("SSML解析错误:", parseError);
        return [{
          speaker_id: defaultSpeakerId,
          tts_text: ssml,
          speed: "1",
        }];
      }

      // 递归处理节点
      function processNodeList(nodeList, speakerId, speed) {
        let buffer = '';
        for (let i = 0; i < nodeList.length; i++) {
          const node = nodeList[i];
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.tagName === 'voice') {
              // 先输出buffer
              if (buffer.trim()) {
                result.push({
                  speaker_id: speakerId,
                  tts_text: `<speak>${buffer}</speak>`,
                  speed: speed,
                });
                buffer = '';
              }
              // 递归处理voice
              const newSpeakerId = node.getAttribute('name') || speakerId;
              processNodeList(node.childNodes, newSpeakerId, speed);
            } else if (node.tagName === 'prosody' && node.hasAttribute('rate')) {
              // 先输出buffer
              if (buffer.trim()) {
                result.push({
                  speaker_id: speakerId,
                  tts_text: `<speak>${buffer}</speak>`,
                  speed: speed,
                });
                buffer = '';
              }
              // prosody内容单独成对象，speed取rate
              const newSpeed = node.getAttribute('rate');
              // prosody内可能还有嵌套voice
              processNodeList(node.childNodes, speakerId, newSpeed);
            } else {
              // 其他标签，保留原标签
              buffer += new XMLSerializer().serializeToString(node);
            }
          } else if (node.nodeType === Node.TEXT_NODE) {
            buffer += node.textContent;
          }
        }
        // 输出剩余buffer
        if (buffer.trim()) {
          result.push({
            speaker_id: speakerId,
            tts_text: `<speak>${buffer}</speak>`,
            speed: speed,
          });
        }
      }

      // 获取speak根节点
      const speakElem = xmlDoc.querySelector("speak");
      if (!speakElem) {
        return [{
          speaker_id: defaultSpeakerId,
          tts_text: ssml,
          speed: "1",
        }];
      }

      processNodeList(speakElem.childNodes, defaultSpeakerId, "1");
      return result;
    },
    unescapeString(str) {
        // 使用JSON.parse来反转义JSON字符串中的特殊字符
        // 注意：这种方法只适用于JSON格式的字符串反转义
        try {
            return JSON.parse('"' + str.replace(/"/g, '\\"') + '"');
        } catch (e) {
            // 如果不是JSON格式的字符串，或者有其他复杂情况，可以手动处理
            return str
                .replace(/\\n/g, '\n')
                .replace(/\\t/g, '\t')
                .replace(/\\"/g, '"')
                .replace(/\\'/g, "'")
                .replace(/\\\\/g, '\\');
        }
    },
    /**
     * 设置编辑器内容并绑定 delete-btn 事件
     * @param {string} htmlOrSSML - 可以是 HTML 或 SSML 字符串
     * @param {string} showContent - 原始显示的内容
     */
    setContent(htmlOrSSML, showContent) {
      let html = htmlOrSSML;

      // 如果是 SSML，则转换为 editor HTML
      if (/<speak[\s>]/i.test(htmlOrSSML)) {
        const match = htmlOrSSML.match(/<speak>([\s\S]*?)<\/speak>/i);
        if (match) {
          html = this.ssmlToEditorHtml(htmlOrSSML);
        }
      }

      // 反转义showContent
      if (showContent) {
        showContent = this.unescapeString(showContent);
        html = showContent
      }

      // 设置内容
      this.content = html;

      // 手动绑定 delete-btn 点击事件
      this.$nextTick(() => {
        const deleteBtns = this.$refs.editableContent.querySelectorAll('.delete-btn');
        deleteBtns.forEach(btn => {
          btn.removeEventListener('click', this.handleDeleteBtnClick);
          btn.addEventListener('click', this.handleDeleteBtnClick);
        });

        // 初始化 phoneme-wrap 特殊行为
        this.$refs.editableContent.querySelectorAll('phoneme-wrap').forEach(node => {
          this.setupPhonemeWrap(node);
        });
      });

      // 更新字数统计
      this.$nextTick(() => {
        this.updateWordCount();
      });
    },
    createSayAsWrap(displayType, interpretAs, content) {
      const wrapDiv = document.createElement('say-as-wrap');
      wrapDiv.setAttribute('contenteditable', 'false');

      // 创建类型标签
      const typeLabel = document.createElement('span');
      typeLabel.className = 'type-label';
      typeLabel.textContent = displayType;
      wrapDiv.appendChild(typeLabel);

      // 使用 createDeleteButton 创建删除按钮
      const deleteBtn = this.createDeleteButton(); // 调用 Vue 组件的方法
      wrapDiv.appendChild(deleteBtn);

      // 添加【】包裹的内容
      wrapDiv.appendChild(document.createTextNode('【'));

      const sayAsElement = document.createElement('say-as');
      sayAsElement.setAttribute('interpret-as', interpretAs);
      sayAsElement.setAttribute('display-type', displayType);
      sayAsElement.textContent = content;
      wrapDiv.appendChild(sayAsElement);

      wrapDiv.appendChild(document.createTextNode('】'));

      // 绑定删除事件
      deleteBtn.addEventListener('click', this.handleDeleteBtnClick);

      return wrapDiv;
    },
    createProsodyWrap(displayType, rate, content) {
      const wrapDiv = document.createElement('prosody-wrap');
      wrapDiv.setAttribute('contenteditable', 'false');

      // 创建类型标签
      const typeLabel = document.createElement('span');
      typeLabel.className = 'type-label speed-label';
      typeLabel.textContent = displayType;
      wrapDiv.appendChild(typeLabel);

      // 创建删除按钮（使用 createDeleteButton 方法）
      const deleteBtn = this.createDeleteButton();
      wrapDiv.appendChild(deleteBtn);

      // 添加【】包裹的内容
      wrapDiv.appendChild(document.createTextNode('【'));

      const prosodyElement = document.createElement('prosody');
      prosodyElement.setAttribute('rate', rate);
      prosodyElement.setAttribute('display-type', displayType);
      prosodyElement.textContent = content;
      wrapDiv.appendChild(prosodyElement);

      wrapDiv.appendChild(document.createTextNode('】'));

      // 绑定删除事件
      deleteBtn.addEventListener('click', this.handleDeleteBtnClick);

      return wrapDiv;
    },
    createVoiceWrap(displayName, speakerId, content) {
      const wrapDiv = document.createElement('voice-wrap');
      wrapDiv.setAttribute('contenteditable', 'false');

      // 创建类型标签
      const typeLabel = document.createElement('span');
      typeLabel.className = 'type-label voice-label';
      typeLabel.textContent = displayName;
      wrapDiv.appendChild(typeLabel);

      // 创建删除按钮
      const deleteBtn = this.createDeleteButton();
      wrapDiv.appendChild(deleteBtn);

      // 添加【】包裹的内容
      wrapDiv.appendChild(document.createTextNode('【'));

      const voiceElement = document.createElement('voice');
      voiceElement.setAttribute('name', speakerId);
      voiceElement.setAttribute('display-type', displayName);
      voiceElement.innerHTML = content;
      wrapDiv.appendChild(voiceElement);

      wrapDiv.appendChild(document.createTextNode('】'));

      // 绑定删除事件
      deleteBtn.addEventListener('click', this.handleDeleteBtnClick);

      return wrapDiv;
    },
    ssmlToEditorHtml(ssml) {
      const that = this;
      if (!ssml) return '';
      
      // 提取<speak>标签中的内容
      const match = ssml.match(/<speak>([\s\S]*?)<\/speak>/i);
      if (!match) return ssml;
      
      let content = match[1];

      // 1. 转换<break>标签
      content = content.replace(/<break\s+([^>]*?)\/?>/gi, function(match, attrs) {
        // 解析属性
        const timeMatch = attrs.match(/time="([^"]+)"/i);
        const time = timeMatch && timeMatch[1] ? timeMatch[1] : '';
        // 找到PAUSE_OPTIONS里value等于time的项的label
        let timeLabel = '';
        PAUSE_OPTIONS.forEach(option => {
          if (option.value === time) {
            timeLabel = option.label;
          }
        })
        
        return `<break-wrap contenteditable="false">
          <break time="${time}" text="${timeLabel}"></break>
        </break-wrap>`;
      });

      // 2. 转换<say-as>标签
      content = content.replace(/<say-as\s+([^>]*?)>([\s\S]*?)<\/say-as>/gi, function(match, attrs, inner) {
        // 解析属性
        const interpretAsMatch = attrs.match(/interpret-as="([^"]+)"/i);
        const interpretAs = interpretAsMatch && interpretAsMatch[1] ? interpretAsMatch[1] : '';
        let displayType = DIGIT_TYPES[interpretAs].label;

        // 调用上面定义的函数生成结构
        const wrapDiv = that.createSayAsWrap(displayType, interpretAs, inner);

        // 将新创建的 DOM 节点转为 HTML 字符串
        const tempDiv = document.createElement('div');
        tempDiv.appendChild(wrapDiv.cloneNode(true));
        return tempDiv.innerHTML;
      });

      return `${content}`;
    },
    mergeTTSArrayToEditorHtml(ttsArray, mainSpeakerId, speakerMap) {
      if (!ttsArray || !ttsArray.length) return '';

      const getDisplayName = (speaker_id) => {
        const found = speakerMap.find(v => v.speaker_id === speaker_id);
        return found ? found.name : speaker_id;
      };

      let html = '<speak>'
      ttsArray.forEach((tts, index) => {
        if (tts.speaker_id === mainSpeakerId) {
          // 去掉头尾的<speak>标签</speak>
          let text = tts.tts_text;
          if (text.startsWith('<speak>') && text.endsWith('</speak>')) {
            text = text.substring(7, text.length - 8)
          }
          // html += text
          // 判断speed是否为1
          if (tts.speed != 1) {
            let speedText = tts.speed * 100
            const wrapDiv = this.createProsodyWrap(`语速:${speedText}`, tts.speed, text);
            const tempDiv = document.createElement('div');
            tempDiv.appendChild(wrapDiv.cloneNode(true));
            html += tempDiv.innerHTML;
          } else {
            html += text
          }
        } else {
          // 不同音色
          let text = tts.tts_text
          if (text.startsWith('<speak>') && text.endsWith('</speak>')) {
            text = text.substring(7, text.length - 8)
          }
          let speakerName = getDisplayName(tts.speaker_id)

          if (tts.speed != 1) {
            let speedText = tts.speed * 100
            const wrapDiv = this.createProsodyWrap(`语速:${speedText}`, tts.speed, text);
            const tempDiv = document.createElement('div');
            tempDiv.appendChild(wrapDiv.cloneNode(true));
            text = tempDiv.innerHTML;
          }

          const wrapDiv = this.createVoiceWrap(speakerName, tts.speaker_id, text);
          const tempDiv = document.createElement('div');
          tempDiv.appendChild(wrapDiv.cloneNode(true));
          html += tempDiv.innerHTML;
        }
      })

      html += '</speak>'
      return html;
    },
    // 添加键盘事件处理
    handleKeyDown(e) {
      // Esc键关闭浮动控件
      if (e.key === 'Escape' || e.keyCode === 27) {
        this.hideFloatingControls()
      }
    },
    getContent() {
      // 获取编辑器内容
      const content = this.$refs.editableContent.innerHTML;
      return content;
    }
  },

  watch: {
    content: {
      handler(newVal) {
        this.hasBreak = newVal.includes('<break')
      },
      immediate: true
    },

    defaultVoice: {
      handler(newVal) {
        this.store.commit('ssmlEditor/SET_DEFAULT_VOICE', newVal)
      },
      immediate: true
    }
  },

  mounted() {
    document.addEventListener('selectionchange', this.saveSelection, true)
    document.addEventListener('click', (e) => {
      const isToolbarClick = e.target.closest('.operators')
      if (!isToolbarClick && !this.isFocusInsideEditor) {
        this.savedRange = null
        this.store.commit('ssmlEditor/SET_RANGE', null)
        this.hideFloatingControls()
      }
    })
    
    // 添加全局自定义事件监听器
    document.addEventListener('ssml-structure-changed', () => {
      // 延迟执行以确保DOM更新
      setTimeout(() => {
        this.handleInput({ target: this.$refs.editableContent })
      }, 10)
    })

    this.$refs.editableContent.addEventListener('click', this.handleDeleteBtnClick, true)
    
    // 监听Esc键，用于关闭浮动控件
    document.addEventListener('keydown', this.handleKeyDown)

    // 添加滚动事件监听
    this.isScrolling = false
    this.scrollTimer = null
    this.$refs.editableContent.addEventListener('scroll', this.handleScroll)
    document.addEventListener('scroll', this.handleScroll)

    // 添加复制事件监听
    this.$refs.editableContent.addEventListener('copy', this.handleCopy);
    
    // 添加剪切事件监听
    this.$refs.editableContent.addEventListener('cut', this.handleCut);

    // MutationObserver监听新添加的phoneme-wrap元素
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeName && node.nodeName.toLowerCase() === 'phoneme-wrap') {
              this.setupPhonemeWrap(node);
            }
          });
        }
      });
    });
    
    observer.observe(this.$refs.editableContent, { childList: true, subtree: true });
    
    // 初始化已存在的phoneme-wrap元素
    this.$refs.editableContent.querySelectorAll('phoneme-wrap').forEach((node) => {
      this.setupPhonemeWrap(node);
    });
  },

  beforeDestroy() {
    document.removeEventListener('selectionchange', this.saveSelection, false)
    document.removeEventListener('ssml-structure-changed', this.handleInput)
    this.$refs.editableContent.removeEventListener('click', this.handleDeleteBtnClick, true)
    
    // 移除Esc键监听
    document.removeEventListener('keydown', this.handleKeyDown)

    // 移除滚动事件监听
    this.$refs.editableContent.removeEventListener('scroll', this.handleScroll)
    document.removeEventListener('scroll', this.handleScroll)
    clearTimeout(this.scrollTimer)

    // 移除复制和剪切事件监听
    this.$refs.editableContent.removeEventListener('copy', this.handleCopy);
    this.$refs.editableContent.removeEventListener('cut', this.handleCut);
  }
}
</script>

<style lang="less" scoped>
.ssml-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  
  .content {
    flex: 1;
    padding: 20px;
    position: relative;
    
    .editable-area {
      height: calc(100% - 30px);
      outline: none;
      font-family: PingFang-SC, PingFang-SC;
      font-weight: bold;
      font-size: 14px;
      color: #050F0F;
      line-height: 30px;
      
      &.placeholder:empty:before {
        content: '请输入文本';
        color: #999;
      }
    }
    
    .input-number {
      position: absolute;
      right: 20px;
      color: #909399;
      
      .warning {
        color: #F56C6C;
      }
    }
  }
}
</style>
<style lang="less">
.ssml-editor {
  // 通用包装器样式
  .temp {
    display: inline-flex;
    align-items: center;
    // color: #41B48C;
  }

  // SSML 标签基础样式
  .ssml-wrap {
    display: inline;
    margin: 0 2px;
    
    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }
  }

  // 停顿标签样式
  break-wrap {
    display: inline-flex;
    align-items: center;
    padding: 0 7px;
    margin: 0 5px;
    height: 24px;
    background: rgba(65, 180, 140, 0.1);
    border-radius: 4px;
    color: #41B48C;
    font-size: 13px;
    white-space: nowrap;
    
    break {
      display: inline-block;
      &::before {
        content: attr(text);
      }
    }
  }

  // 连续标签样式
  recycle-wrap {
    display: inline-flex;
    align-items: center;
    padding: 0 7px;
    margin: 0 2px;
    height: 24px;
    background: rgba(65,180,140,0.1);
    border-radius: 4px;
    color: #41B48C;
    font-size: 13px;
    white-space: nowrap;
    
    recycle {
      display: inline-block;
      &::before {
        content: attr(text);
      }
    }
  }

  // 数字标签样式
  say-as-wrap {
    display: inline-flex;
    color: #41B48C;
    align-items: center;
  }

  say-as {
    color: #050F0F;
    position: relative;
  }

  .type-label {
    height: 24px;
    display: inline-flex;
    align-items: center;
    padding: 0 4px 0 7px;
    background: rgba(65,180,140,0.1);
    border-radius: 4px 0 0 4px;
    font-size: 13px;
    font-family: PingFang-SC;
    font-weight: 500;
    color: #41B48C;
    margin-left: 5px;
  }

  // 删除按钮样式
  .delete-btn {
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center; // 添加水平居中
    color: #41B48C;
    cursor: pointer;
    background: rgba(65,180,140,0.1);
    padding: 0 7px 0 0;
    border-radius: 0 4px 4px 0;

    img {
      vertical-align: middle;
    }
  }

  // 变速标签样式
  prosody-wrap {
    display: inline;
    color: #41B48C;
  }

  prosody {
    color: #050F0F;
  }

  // 多人配音标签样式
  voice-wrap {
    display: inline;
    color: #41B48C;
  }

  voice {
    color: #050F0F;
  }

  // 多音字标签样式
  phoneme-label {
    /* 多音字特定样式 */
    background: rgba(115, 35, 255, 0.1);
    color: #7323ff;
  }

  // 多音字包装器样式
  phoneme-wrap {
    position: relative;
    display: inline-block;
    background-color: rgba(129, 199, 132, 0.1);
    border-radius: 3px;
    border-bottom: 1px dashed #81c784;
    padding: 0 2px;
    margin: 0 2px;
  }

  phoneme-wrap .phoneme-label {
    color: #4caf50;
    font-size: 12px;
    margin-left: 4px;
    user-select: none;
  }

  phoneme-wrap .delete-btn {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 16px;
    height: 16px;
    line-height: 16px;
    background-color: #f44336;
    color: white;
    border-radius: 50%;
    font-size: 12px;
    text-align: center;
    cursor: pointer;
    display: none;
    user-select: none;
  }

  phoneme-wrap:hover .delete-btn {
    display: block;
  }
}
</style>